<template>
  <section ref="root" class="financial__panel financial__group" aria-labelledby="financial-group-title">
    <header class="financial__detail-head">
      <button class="financial__back-link" type="button" @click="back">
        <mdicon name="arrow-left" :size="18" aria-hidden="true" /> Back to customers
      </button>
      <h1 id="financial-group-title" tabindex="-1">{{ title }}</h1>
      <p class="financial__help">
        {{ numberLabel(customerEntries.length || review.shipments) }} {{ (customerEntries.length || review.shipments) === 1 ? 'shipment' : 'shipments' }} · {{ numberLabel(review.rows) }} cost rows
        <template v-if="review.bookedFrom"> · booked <span class="financial__nowrap">{{ review.bookedFrom }} – {{ review.bookedTo }}</span></template>
        <template v-if="review.chargeFrom"> · charges <span class="financial__nowrap">{{ review.chargeFrom }} – {{ review.chargeTo }}</span></template>
      </p>
      <!-- The invoice files the costs came from, up front, so the source is known before anything is approved. -->
      <template v-if="review.files.length">
        <span id="financial-group-files" class="visually-hidden">Costs from</span>
        <ul class="financial__group-files" aria-labelledby="financial-group-files">
          <li v-for="file in review.files" :key="file.id">
            <strong>{{ file.label }}</strong> · {{ file.fileName }} · imported {{ when(file.createdAt) }} by {{ file.createdByName }} ·
            {{ fileRows(file) }}
          </li>
        </ul>
      </template>
    </header>

    <div class="financial__price-grid financial__price-grid--two" role="group" aria-label="Separate customer price sources">
      <div><span>Quoted customer prices (SEK)</span><strong>{{ quotedAmount }}</strong><small>Original shipment prices from booking</small></div>
      <div><span>Actual carrier cost</span><strong>{{ amount(group.cost) }}</strong><small>Imported carrier charges · internal only</small></div>
    </div>
    <!-- One decision bar: each total once, and one primary action. -->
    <div class="financial__group-bar">
      <dl class="financial__group-totals">
        <div>
          <dt>Reviewed customer amount</dt>
          <dd>{{ amount(group.price) }}</dd>
        </div>
        <div>
          <dt>Margin</dt>
          <dd :class="{ financial__negative: isNegative(group.margin) }">
            {{ group.margin === null ? '—' : difference(group.margin) }}<small v-if="marginPercent"> · {{ marginPercent }} on cost</small>
          </dd>
        </div>
      </dl>
      <div class="financial__actions">
        <button
          v-if="canApprove"
          id="financial-group-approve"
          class="financial__button financial__button--primary"
          type="button"
          :disabled="busy || save === 'saving'"
          @click="ask"
        >
          {{ approveLabel }}
        </button>
        <button class="financial__button" :class="{ 'financial__button--primary': !canApprove }" type="button" @click="bill">
          Bill {{ title }} <mdicon name="arrow-down" :size="18" aria-hidden="true" />
        </button>
      </div>
    </div>
    <p v-if="billingText" class="financial__group-billing">{{ billingText }}</p>
    <div
      v-if="confirming"
      class="financial__confirm"
      role="alertdialog"
      aria-labelledby="financial-group-confirm-title"
      aria-describedby="financial-group-confirm-text"
    >
      <div>
        <strong id="financial-group-confirm-title">
          {{ confirmTitle }}
        </strong>
        <p id="financial-group-confirm-text">
          Each row is billed at the customer price in the grid: PacSort's price, or the price you set.
          {{ group.toExclude ? `${group.toExclude} ${group.toExclude === 1 ? 'row is' : 'rows are'} excluded with your reason.` : '' }}
          {{ group.check ? `The ${group.check} ${group.check === 1 ? 'row that needs a check stays' : 'rows that need a check stay'} open.` : '' }}
        </p>
      </div>
      <div class="financial__actions">
        <button
          id="financial-group-confirm-button"
          class="financial__button financial__button--primary"
          type="button"
          :disabled="busy || save === 'saving'"
          @click="approve"
        >
          {{ group.ready ? `Approve ${numberLabel(group.ready)} ${group.ready === 1 ? 'row' : 'rows'}` : 'Confirm' }}{{
            group.toExclude ? ` and exclude ${numberLabel(group.toExclude)}` : ''
          }}
        </button>
        <button class="financial__button" type="button" :disabled="busy" @click="cancel">Cancel</button>
      </div>
    </div>
    <p v-if="failure" class="financial__alert financial__alert--error" role="alert">{{ failure }}</p>
    <p v-else-if="notice" class="financial__message" role="status">{{ notice }}</p>

    <!-- What keeps rows from being approved, with a link that filters the grid to them. -->
    <div v-if="review.attention.length" class="financial__alert financial__group-checks" role="status">
      <mdicon name="alert-outline" :size="18" aria-hidden="true" />
      <span>{{ checksText }}</span>
      <button v-if="filter !== 'check'" class="financial__link" type="button" @click="showChecks">Show them</button>
    </div>

    <!-- Every delivery level and every fee with its price basis, above the grid, to check against the agreement. -->
    <section v-if="priceLines.length" class="financial__group-section" aria-labelledby="financial-group-prices">
      <h2 id="financial-group-prices">Prices</h2>
      <p class="financial__help">Check delivery levels against the agreement. Excluded rows are left out.</p>
      <div class="financial__table-wrap">
        <table>
          <thead>
            <tr>
              <th scope="col">Charge</th>
              <th scope="col">Price basis</th>
              <th scope="col">Rows</th>
              <th scope="col">Carrier cost</th>
              <th scope="col">Customer price</th>
              <th scope="col">Margin</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="line in priceLines" :key="line.key">
              <td>
                <strong>{{ line.charge }}</strong><small v-if="line.detail" :class="{ financial__attention: line.missing }">{{ line.detail }}</small>
              </td>
              <td>{{ line.basis }}</td>
              <td class="financial__numeric">{{ numberLabel(line.rows) }}</td>
              <td class="financial__numeric financial__nowrap">{{ amount(line.cost) }}</td>
              <td class="financial__numeric financial__nowrap">
                {{ amount(line.price) }}<small v-if="line.unpriced" class="financial__attention">{{ line.unpriced }} without a price</small>
              </td>
              <td class="financial__numeric financial__nowrap" :class="{ financial__negative: isNegative(line.margin) }">
                {{ line.margin === null ? '—' : difference(line.margin) }}<small v-if="line.percent">{{ line.percent }} on cost</small>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>

    <section class="financial__group-section" aria-labelledby="financial-group-grid">
      <h2 id="financial-group-grid">Rows</h2>
      <ReviewGrid
        v-model:filter="filter"
        :rows="gridRows"
        :currency="currency"
        :save="save"
        :can-undo="canUndo"
        :can-redo="canRedo"
        @edit="edit"
        @undo="undo"
        @redo="redo"
        @retry="retry"
        @open-shipment="openShipment"
      />
    </section>

    <!-- Billing follows the grid on the same page: what was approved above is exported here. -->
    <p class="financial__field-hint">Billing below includes all saved work for this customer through its own cut-off, including older unbilled amounts. The booking period above limits the review only.</p>
    <CustomerBilling
      :imports="imports"
      :entries="billingEntries"
      :quotes="quotes"
      :busy="busy"
      :customer="group.id"
      :name="title"
      :period="period"
      :state="state"
      :notice="billingNotice"
      :failure="billingFailure"
      @select="select"
      @export="exportRows"
    />
  </section>
</template>
<script lang="ts">
import type { FinancialGridFilter } from '@/types/Components/FinancialGrid'
import type { FinancialGroupPriceLine } from '@/types/Components/FinancialGroupReview'
import type {
  FinancialBillingSelection,
  FinancialChargeRole,
  FinancialGridEdit,
  FinancialGridRow,
  FinancialGroupFile,
  FinancialGroupReview,
  FinancialImport,
  FinancialLedgerEntry,
  FinancialQuoteBilling,
  FinancialLine,
  FinancialLineState,
  FinancialOverviewRow,
} from '@/types/Models/Financial'
import { computed, defineComponent, nextTick, ref, watch, type ComputedRef, type PropType, type Ref } from 'vue'
import {
  financialAmountLabel,
  financialDifferenceLabel,
  financialPercent,
  subtractFinancialAmounts,
  sumFinancialAmounts,
} from '@/services/FinancialAmounts'
import ReviewGrid from '@/views/Staff/Financial/Components/ReviewGrid.vue'
import CustomerBilling from '@/views/Staff/Financial/Components/CustomerBilling.vue'

export default defineComponent({
  name: 'FinancialGroupReview',
  components: { CustomerBilling, ReviewGrid },
  props: {
    title: { type: String, required: true },
    group: { type: Object as PropType<FinancialOverviewRow>, required: true },
    review: { type: Object as PropType<FinancialGroupReview>, required: true },
    busy: { type: Boolean, default: false },
    notice: { type: String, default: '' },
    failure: { type: String, default: '' },
    currency: { type: String, default: 'SEK' },
    gridRows: { type: Array as PropType<FinancialGridRow[]>, default: () => [] },
    save: { type: String as PropType<'idle' | 'saving' | 'saved' | 'error'>, default: 'idle' },
    canUndo: { type: Boolean, default: false },
    canRedo: { type: Boolean, default: false },
    // The Bill section: every saved invoice, the billing period, each row's approval state and the export's result.
    imports: { type: Array as PropType<FinancialImport[]>, default: () => [] },
    billingEntries: { type: Array as PropType<FinancialLedgerEntry[]>, default: () => [] },
    entries: { type: Array as PropType<FinancialLedgerEntry[]>, default: () => [] },
    quotes: { type: Array as PropType<FinancialQuoteBilling[]>, default: () => [] },
    period: {
      type: Object as PropType<Omit<FinancialBillingSelection, 'customerId'>>,
      default: () => ({ from: '', to: '', onlyNew: true }),
    },
    state: {
      type: Function as PropType<(line: FinancialLine) => FinancialLineState>,
      default: () => ({ open: true, edited: false, exclude: false, eligible: false, reason: null, price: null }),
    },
    billingNotice: { type: String, default: '' },
    billingFailure: { type: String, default: '' },
  },
  emits: ['back', 'approve', 'open-shipment', 'edit', 'undo', 'redo', 'retry', 'select', 'export'],
  setup(props, { emit }) {
    const root: Ref<HTMLElement | null> = ref(null)
    const confirming: Ref<boolean> = ref(false)
    // The grid opens on the rows that need a check, when there are any.
    const filter: Ref<FinancialGridFilter> = ref(props.group.check ? 'check' : 'all')
    const roleNames: Record<FinancialChargeRole, string> = { base: 'Delivery', additional: 'Additional charge', credit: 'Credit' }
    const amount = (value: string | null): string => financialAmountLabel(value, props.currency)
    const customerEntries: ComputedRef<FinancialLedgerEntry[]> = computed(() => props.entries.filter((entry) => entry.shipment.billingId === props.group.id))
    const quotedAmount: ComputedRef<string> = computed(() => financialAmountLabel(props.group.quotedPrice, 'SEK'))
    const difference = (value: string): string => financialDifferenceLabel(value, props.currency)
    const numberLabel = (value: number): string => value.toLocaleString('en-GB')
    const isNegative = (value: string | null): boolean => value !== null && Number(value) < 0
    const percent = (margin: string | null, cost: string | null): string | null =>
      margin === null || cost === null ? null : financialPercent(margin, cost)
    const roleLabel = (role: FinancialChargeRole): string => roleNames[role]
    const gramsLabel = (min: number | null, max: number | null): string =>
      min === null || max === null ? '' : min === max ? `${numberLabel(min)} g` : `${numberLabel(min)}–${numberLabel(max)} g`
    const sum = (values: (string | null)[]): string | null =>
      values.some((value) => value === null) ? null : sumFinancialAmounts(values as string[])
    // The customer's rows in the file, and how many of them are excluded: the header's row count leaves those out.
    const fileRows = (file: FinancialGroupFile): string =>
      [
        `${numberLabel(file.rows)} ${file.rows === 1 ? 'row' : 'rows'}`,
        file.excluded ? `${numberLabel(file.excluded)} excluded` : '',
      ]
        .filter(Boolean)
        .join(' · ')
    const when = (value: string): string =>
      new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Stockholm' })
    watch(
      () => props.group.id,
      () => {
        filter.value = props.group.check ? 'check' : 'all'
      },
    )
    // A confirmation covers exactly the rows and amount shown: close it when they change or an edit is being saved.
    watch(
      () => [props.group.ready, props.group.readyAmount, props.group.toExclude, props.save],
      () => {
        confirming.value = false
      },
    )
    const canApprove: ComputedRef<boolean> = computed(() => !!props.group.ready || !!props.group.toExclude)
    // One Prices table: each delivery price level, then the deliveries without a price, then each fee and credit.
    const priceLines: ComputedRef<FinancialGroupPriceLine[]> = computed(() => {
      const levels: FinancialGroupPriceLine[] = props.review.prices.map((level) => ({
        key: `level:${level.price}:${level.service}`,
        charge: 'Delivery',
        detail: [level.service, gramsLabel(level.minGrams, level.maxGrams)].filter(Boolean).join(' · '),
        missing: false,
        basis: `${level.basis} · ${amount(level.price)} each`,
        rows: level.shipments,
        cost: level.cost,
        price: level.total,
        unpriced: 0,
        margin: level.margin,
        percent: percent(level.margin, level.cost),
      }))
      // The levels hold every priced delivery, so what is left of the delivery cost has no price yet.
      const bases = props.review.charges.filter((charge) => charge.role === 'base')
      const unpriced = bases.reduce((total, charge) => total + charge.unpriced, 0)
      const baseCost = sum(bases.map((charge) => charge.cost))
      const levelCost = sum(levels.map((level) => level.cost))
      const missing: FinancialGroupPriceLine[] = unpriced
        ? [
            {
              key: 'delivery:unpriced',
              charge: 'Delivery',
              detail: 'No PacSort price',
              missing: true,
              basis: 'Set a customer price in the grid',
              rows: unpriced,
              cost: baseCost === null || levelCost === null ? null : subtractFinancialAmounts(baseCost, levelCost),
              price: null,
              unpriced: 0,
              margin: null,
              percent: null,
            },
          ]
        : []
      const fees: FinancialGroupPriceLine[] = props.review.charges
        .filter((charge) => charge.role !== 'base')
        .map((charge) => ({
          key: `${charge.role}:${charge.description}`,
          charge: charge.description,
          detail: roleLabel(charge.role),
          missing: false,
          basis: charge.basis,
          rows: charge.rows,
          cost: charge.cost,
          price: charge.price,
          unpriced: charge.unpriced,
          margin: charge.margin,
          percent: charge.unpriced ? null : percent(charge.margin, charge.cost),
        }))
      return [...levels, ...missing, ...fees]
    })
    const marginPercent: ComputedRef<string | null> = computed(() => percent(props.group.margin, props.group.pricedCost))
    const billingText: ComputedRef<string> = computed(() =>
      [
        props.group.toBill ? `${numberLabel(props.group.toBill)} approved, not billed` : '',
        props.group.billed ? `${numberLabel(props.group.billed)} billed` : '',
      ]
        .filter(Boolean)
        .join(' · '),
    )
    const checksText: ComputedRef<string> = computed(() => {
      const rows = props.review.attention.reduce((total, item) => total + item.rows, 0)
      const reasons = props.review.attention.map((item) => `${item.reason} (${numberLabel(item.rows)})`).join(' · ')
      return `${numberLabel(rows)} ${rows === 1 ? 'row needs' : 'rows need'} a check: ${reasons}`
    })
    const approveLabel: ComputedRef<string> = computed(() => {
      const parts = [
        props.group.ready ? `Approve ${numberLabel(props.group.ready)} ${props.group.ready === 1 ? 'row' : 'rows'} (${amount(props.group.readyAmount)})` : '',
        props.group.toExclude ? `exclude ${numberLabel(props.group.toExclude)}` : '',
      ].filter(Boolean)
      const text = parts.join(' and ')
      return text.charAt(0).toUpperCase() + text.slice(1)
    })
    const confirmTitle: ComputedRef<string> = computed(() =>
      [
        props.group.ready
          ? `Approve ${numberLabel(props.group.ready)} ${props.group.ready === 1 ? 'row' : 'rows'} of ${props.title} (${amount(props.group.readyAmount)})`
          : '',
        props.group.toExclude ? `exclude ${numberLabel(props.group.toExclude)}` : '',
      ]
        .filter(Boolean)
        .join(' and ') + '?',
    )
    const ask = async (): Promise<void> => {
      confirming.value = true
      await nextTick()
      document.getElementById('financial-group-confirm-button')?.focus()
    }
    const cancel = async (): Promise<void> => {
      confirming.value = false
      await nextTick()
      document.getElementById('financial-group-approve')?.focus()
    }
    const approve = async (): Promise<void> => {
      confirming.value = false
      emit('approve')
      await nextTick()
      document.getElementById('financial-group-title')?.focus()
    }
    const edit = (edits: FinancialGridEdit[]): void => {
      emit('edit', edits)
    }
    const undo = (): void => {
      emit('undo')
    }
    const redo = (): void => {
      emit('redo')
    }
    const retry = (): void => {
      emit('retry')
    }
    const showChecks = async (): Promise<void> => {
      filter.value = 'check'
      await nextTick()
      root.value?.querySelector<HTMLElement>('.financial__grid')?.focus()
    }
    const back = (): void => {
      emit('back')
    }
    const openShipment = (itemId: string): void => {
      emit('open-shipment', itemId)
    }
    // "Bill {customer}" moves to the Bill section below the grid.
    const bill = (): void => {
      document.getElementById('financial-billing-title')?.focus()
      document.getElementById('financial-billing-title')?.scrollIntoView({ block: 'start' })
    }
    const select = (selection: FinancialBillingSelection): void => {
      emit('select', selection)
    }
    const exportRows = (kind: 'billing' | 'specification', selection: FinancialBillingSelection, lines: FinancialLine[]): void => {
      emit('export', kind, selection, lines)
    }
    return {
      quotedAmount,
      customerEntries,
      root,
      confirming,
      filter,
      amount,
      difference,
      numberLabel,
      isNegative,
      when,
      fileRows,
      canApprove,
      priceLines,
      marginPercent,
      billingText,
      checksText,
      approveLabel,
      confirmTitle,
      ask,
      cancel,
      approve,
      edit,
      undo,
      redo,
      retry,
      showChecks,
      back,
      openShipment,
      bill,
      select,
      exportRows,
    }
  },
})
</script>
